import {Trans, Plural, plural, t} from '@lingui/macro'
import {Formik} from 'formik'
import {AnimatePresence, motion} from 'framer-motion'
import React, {useState} from 'react'

import {
    Button,
    Checkbox,
    Inline,
    Stack,
    Text,
    LoadingOverlay,
    tokens,
    FormikInput as Input,
} from '@pleo-io/telescope'

import config from 'config'
import background from 'images/bgn-login.png'
import googleLogo from 'images/google-logo.svg'
import incorrectPinImage from 'images/pin/incorrect.png'
import AuthAppIllustration from 'images/sca/auth-app-bird.svg'
import ExpiredIllustration from 'images/sca/expired-bird.svg'
import PasscodeIllustration from 'images/sca/passcode-bird.svg'
import VerifyIllustration from 'images/sca/verify-bird.svg'
import ModalResetPassword from 'modals/reset-password/reset-password'
import {isRequestError} from '~/api'
import {OtpType} from '~/api/auth/register'
import {ContactSupport} from '~/feature-ui/contact-support'
import {ColumnImage} from '~/generic-ui/layout/page/composition.styles'
import {getErrorMessage, notifyError} from '~/shell/error-handler'
import {ExternalProvider, useOauthUrl} from '~/shell/oauth'
import {useLogin} from '~/shell/session'
import {useToaster} from '~/shell/toaster'

import {
    convertThrottledToMinutes,
    getTimeThrottled,
    LoginScreen,
    useLoginLocationState,
} from './helpers'

import {ErrorMessageType, RESET_ERROR_DELAY, useOtpResendTimer, useOtpNotifications} from '..'
import {passwordValidationSchema} from '../../login/login.validation'
import {checkEmail, OTP_EXPIRATION_TIME, requestOtp} from '../api'
import {CodeInput} from '../code-input'
import {EmailInput} from '../email-input'
import {PasscodeInput} from '../passcode-input'
import * as s from '../styles'
import {useTimer} from '../timer/use-timer'

const commercialWebUrl = config.endpoints.commercial

export function EmailScreen() {
    const {state: locationState, update, clear} = useLoginLocationState()
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)
    const {showToast} = useToaster()

    const navigateToNextStep = ({legacy, email}: {legacy?: boolean; email: string}) => {
        if (legacy) {
            update({
                email,
                screen: LoginScreen.PASSWORD,
            })
        } else {
            update({
                email,
                screen: LoginScreen.PASSCODE,
            })
        }
    }

    // If SSO with google we navigate to the validation screen
    React.useEffect(() => {
        const email = locationState?.email
        const loginToken = locationState?.loginToken
        if (!loginToken || !email || isLoading || hasError) {
            return
        }

        const requestOtpAndNavigateToNextStep = async () => {
            try {
                // We set this isLoading state to avoid requesting the otp more than once.
                // There's no need to update the isLoading state after this request is resolved since the user is already on the next screen
                setIsLoading(true)
                const response = await requestOtp({email, loginToken})
                update({
                    ...response,
                    email: locationState.email,
                    screen: LoginScreen.VALIDATION,
                })
            } catch (error) {
                const message = getErrorMessage(error)
                setHasError(!!message)
                setIsLoading(false)
                update({
                    email: null,
                    screen: LoginScreen.EMAIL,
                })
                notifyError(error)
            }
        }

        requestOtpAndNavigateToNextStep()
    }, [locationState.loginToken, locationState.email, update, isLoading, hasError, clear])

    const onSubmitEmail = async (email: string) => {
        setIsLoading(true)
        try {
            const response = await checkEmail(email)
            setIsLoading(false)
            navigateToNextStep({...response, email})
        } catch (error) {
            setIsLoading(false)
            showToast(t`Please try again`, {
                title: t`Something went wrong.`,
                level: 'error',
            })
            update({email: null})
            notifyError(error)
        }
    }

    const googleUrl = useOauthUrl({
        action: 'external',
        provider: ExternalProvider.GOOGLE,
        scope: ['resource:basic'],
        register: false,
    })

    if (!googleUrl) {
        return null
    }

    if (locationState.loginToken && !hasError) {
        return <LoadingOverlay data-testid="loading-indicator" />
    }

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo />
                <s.Form>
                    <section>
                        <Stack space={18} stretch>
                            <Stack space={32} stretch>
                                <Text variant="3xlarge-accent">
                                    <Trans>Log in to Pleo</Trans>
                                </Text>
                                <s.OauthButton href={googleUrl}>
                                    <img src={googleLogo} alt={t`Google logo`} />
                                    <Trans>Continue with Google</Trans>
                                </s.OauthButton>
                                <s.DividerWrapper>
                                    <s.Divider>
                                        <Trans>or</Trans>
                                    </s.Divider>
                                </s.DividerWrapper>
                            </Stack>
                            <EmailInput
                                isLoading={Boolean(locationState.email && isLoading)}
                                initialValue={locationState.email}
                                onSubmit={onSubmitEmail}
                            />
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Trans>
                        No account?{' '}
                        <Button variant="link" href={commercialWebUrl}>
                            Get started for free
                        </Button>
                    </Trans>
                </s.BottomNav>
            </s.Content>
            <ColumnImage $imagePath={background} />
        </s.Wrapper>
    )
}

type PasswordFormValues = {
    password: string
}

export function PasswordScreen() {
    const {state: locationState, update} = useLoginLocationState()
    const [showPassword, setShowPassword] = useState(false)

    const formUrl = useOauthUrl({
        action: 'login',
        scope: ['resource:basic'],
        register: false,
    })

    const navigateBack = () => {
        update({
            screen: LoginScreen.EMAIL,
        })
    }

    const initialValues = {
        password: '',
    }

    if (!formUrl) {
        return null
    }

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo />
                <s.Form>
                    <section>
                        <Formik<PasswordFormValues>
                            initialValues={initialValues}
                            validateOnBlur
                            validateOnMount
                            validationSchema={passwordValidationSchema}
                            onSubmit={() => {}}
                        >
                            {(formik) => {
                                return (
                                    <form noValidate action={formUrl} method="POST">
                                        <input
                                            type="hidden"
                                            name="email"
                                            value={locationState.email ?? ''}
                                        />
                                        <Stack space={40} stretch>
                                            <Text variant="3xlarge-accent">
                                                <Trans>Password</Trans>
                                            </Text>
                                            <Stack space={14} stretch>
                                                <s.PasswordInputWrapper>
                                                    <Input
                                                        autoFocus
                                                        aria-label={t`Enter password`}
                                                        name="password"
                                                        placeholder={t`Enter password`}
                                                        type={showPassword ? 'text' : 'password'}
                                                        autoComplete="off"
                                                        variant="naked"
                                                    />
                                                    <s.StyledNakedButton
                                                        type="button"
                                                        onClick={() =>
                                                            setShowPassword(!showPassword)
                                                        }
                                                    >
                                                        <s.StyledSeenIcon
                                                            showPassword={showPassword}
                                                        />
                                                    </s.StyledNakedButton>
                                                </s.PasswordInputWrapper>

                                                <Inline alignX="right">
                                                    <Button
                                                        variant="link"
                                                        type="button"
                                                        onClick={() => ModalResetPassword.show()}
                                                    >
                                                        <Trans>Forgot password?</Trans>
                                                    </Button>
                                                </Inline>
                                            </Stack>
                                            <Button
                                                disabled={!formik.isValid || formik.isSubmitting}
                                                variant="primary"
                                                type="submit"
                                                data-testid="login-submit-button"
                                            >
                                                <Trans>Log in</Trans>
                                            </Button>
                                        </Stack>
                                    </form>
                                )
                            }}
                        </Formik>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Button variant="link" onClick={navigateBack}>
                        &larr; <Trans>Back</Trans>
                    </Button>
                </s.BottomNav>
            </s.Content>
            <ColumnImage $imagePath={background} />
        </s.Wrapper>
    )
}

export function PasscodeScreen() {
    const {
        state: {email},
        update,
    } = useLoginLocationState()
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)
    const [canRetry, setCanRetry] = React.useState(true)
    const [timeThrottled, setTimeThrottled] = React.useState(0)
    const minutesThrottled = convertThrottledToMinutes(timeThrottled)
    const notifications = useOtpNotifications()

    const navigateBack = () => {
        setIsLoading(false)
        setHasError(false)
        setCanRetry(true)
        update({
            passcode: null,
            phoneLastDigits: undefined,
            googleAuth: undefined,
            screen: LoginScreen.EMAIL,
        })
    }

    const onSetPasscode = async (passcode: string) => {
        if (!email || !passcode) {
            return
        }
        try {
            setIsLoading(true)
            const response = await requestOtp({email, passcode})
            setIsLoading(false)
            update({
                ...response,
                passcode,
                screen: LoginScreen.VALIDATION,
            })
        } catch (err) {
            const message = getErrorMessage(err)
            setHasError(!!message)
            setIsLoading(false)
            if (isRequestError(err) && message === ErrorMessageType.throttled) {
                const throttled = getTimeThrottled(err.response)
                setCanRetry(false)
                setTimeThrottled(throttled)
            } else if (message === ErrorMessageType.phone_otp_limit_exceeded) {
                notifications[ErrorMessageType.phone_otp_limit_exceeded]()
                navigateBack()
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    setHasError(false)
                    update({
                        phoneLastDigits: undefined,
                        passcode: null,
                    })
                }, RESET_ERROR_DELAY)
            }
            notifyError(err)
        }
    }

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo />
                <s.Form>
                    <section>
                        <Stack space={40} stretch>
                            {isLoading && <LoadingOverlay data-testid="loading-indicator" />}
                            {!hasError && (
                                <>
                                    <Text variant="3xlarge-accent" align="center">
                                        <Trans>Enter passcode</Trans>
                                    </Text>
                                    <PasscodeInput onSubmit={onSetPasscode} />
                                </>
                            )}
                            {hasError && canRetry && (
                                <>
                                    <Text variant="3xlarge-accent" align="center">
                                        <Trans>Incorrect passcode</Trans>
                                    </Text>
                                    <s.PinIllustration>
                                        <img src={incorrectPinImage} alt={t`Incorrect Pin`} />
                                    </s.PinIllustration>
                                </>
                            )}
                            {hasError && !canRetry && (
                                <>
                                    <Text variant="3xlarge-accent" align="center">
                                        <Trans>Too many wrong attempts.</Trans>
                                    </Text>
                                    <Text variant="2xlarge-accent" align="center">
                                        <Plural
                                            value={minutesThrottled}
                                            _0=""
                                            one="You can try again in # minute"
                                            other="You can try again in # minutes"
                                        />
                                    </Text>
                                    <s.PinIllustration>
                                        <img src={incorrectPinImage} alt={t`Incorrect Pin`} />
                                    </s.PinIllustration>
                                </>
                            )}
                            <ContactSupport
                                chatLabel={t`Forgot passcode?`}
                                emailLabel={t`Forgot passcode? Contact support@pleo.io`}
                            />
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Button variant="link" onClick={navigateBack}>
                        &larr; <Trans>Back</Trans>
                    </Button>
                </s.BottomNav>
            </s.Content>
            <ColumnImage>
                <img src={PasscodeIllustration} alt="" />
            </ColumnImage>
        </s.Wrapper>
    )
}

/**
 * This component handles the login flow with a One Time Password (OTP)
 */
export function ValidationScreen() {
    const {
        state: {email, loginToken, trust, passcode, phoneLastDigits, googleAuth, otp: previousOtp},
        clear,
        update,
    } = useLoginLocationState()
    const [isLoading, setIsLoading] = React.useState(false)
    const [isTokenSent, setIsTokenSent] = React.useState(true)
    const [isResendingToken, setIsResendingToken] = React.useState(false)
    const [canResendToken, setCanResendToken] = React.useState(true)
    const [hasError, setHasError] = React.useState(false)
    const [hasTokenSentError, setHasTokenSentError] = React.useState(false)
    const [isTrusted, setIsTrusted] = React.useState(trust || false)
    const resendTimer = useOtpResendTimer()
    const otpType = phoneLastDigits ? OtpType.PHONE : OtpType.GOOGLE_AUTH
    const {showToast} = useToaster()
    const notifications = useOtpNotifications()

    const login = useLogin()

    // Here we check to ensure that the otp is sent successfully
    const isOtpSent = !hasTokenSentError && isTokenSent

    // When the token is sent we begin a countdown timer.
    // Each token is valid for 10 minutes.
    const timer = useTimer(isOtpSent ? OTP_EXPIRATION_TIME : 0)

    const isTokenVerifying = timer.currentTime !== 0

    const isTokenExpired = isTokenSent && !hasError && timer.currentTime === 0

    const isResendDisabled =
        (timer.elaspedTime === 0 && !hasError) || resendTimer.isResendDisabled || !canResendToken

    const navigateBack = () => {
        if (loginToken) {
            // Resetting this takes use to the email screen
            // At this point we'll have the email prefilled.
            update({screen: LoginScreen.EMAIL})
            return
        }
        // Resetting this takes the user back to the the passcode screen
        update({
            screen: LoginScreen.PASSCODE,
            otp: null,
            passcode: null,
            phoneLastDigits: undefined,
            googleAuth: undefined,
        })
    }

    const resendToken = async () => {
        if (!email || (!passcode && !loginToken) || isResendingToken || !canResendToken) {
            return
        }

        try {
            // Ensure we clear the formerly expired code here on resend
            update({otp: null})
            setIsResendingToken(true)
            setHasError(false)
            resendTimer.handleDisableResend()
            const response = await requestOtp({email, loginToken, passcode})
            setIsResendingToken(false)
            setIsTokenSent(true)
            update({
                ...response,
            })
        } catch (err) {
            const message = getErrorMessage(err)
            setHasTokenSentError(!!message)
            setIsLoading(false)
            if (isRequestError(err) && message === ErrorMessageType.throttled) {
                const throttled = getTimeThrottled(err.response)
                setCanResendToken(false)
                showToast(t`Too many requests`, {
                    title: plural(throttled, {
                        one: `Try again in # minute.`,
                        other: `Try again in # minutes.`,
                    }),
                    level: 'error',
                })
            }

            if (message === ErrorMessageType.phone_otp_limit_exceeded) {
                notifications[ErrorMessageType.phone_otp_limit_exceeded]()
                navigateBack()
            }
            notifyError(err)
        }
    }

    const onSubmitOtp = async (otp: string) => {
        if (!email || (!passcode && !loginToken) || isLoading) {
            return
        }

        try {
            setIsLoading(true)
            update({otp})
            await login({
                email,
                loginToken: loginToken ?? null,
                passcode: passcode ?? null,
                otp,
                trust: isTrusted,
                otpType,
            })
            setIsLoading(false)
            // Here we clear the location state, removing all keys
            // We delay so the screen state remains for a second longer
            setTimeout(() => clear(), 1000)
        } catch (error) {
            const message = getErrorMessage(error)
            setIsLoading(false)
            setHasError(true)
            if (isRequestError(error) && message === ErrorMessageType.throttled) {
                const throttled = getTimeThrottled(error.response)
                showToast(t`Too many wrong attempts`, {
                    title: plural(throttled, {
                        one: `Try again in # minute.`,
                        other: `Try again in # minutes.`,
                    }),
                    level: 'error',
                })
            } else if (message === ErrorMessageType.phone_otp_limit_exceeded) {
                notifications[ErrorMessageType.phone_otp_limit_exceeded]()
                navigateBack()
            } else {
                showToast(t`Please try again`, {
                    title: t`Wrong credentials`,
                    level: 'error',
                })
            }
            notifyError(error)
        }
    }

    const showTokenValidationLoader = (isResendingToken && !hasError) || isLoading

    return (
        <s.Wrapper>
            <s.Content>
                <s.Logo />
                <s.Form>
                    <section>
                        <Stack space={24} stretch>
                            {showTokenValidationLoader && (
                                <LoadingOverlay data-testid="loading-indicator" />
                            )}
                            {!isTokenExpired && (
                                <>
                                    <Text variant="3xlarge-accent">
                                        <Trans>Enter verification code</Trans>
                                    </Text>
                                    <Text variant="medium-default">
                                        {!googleAuth && (
                                            <>
                                                <Trans>
                                                    Check your phone and enter the 6-digit code
                                                    we've sent to your number ending in{' '}
                                                    {phoneLastDigits}.
                                                </Trans>{' '}
                                                <ContactSupport
                                                    chatLabel={t`New number?`}
                                                    emailLabel={t`New number? Contact support@pleo.io`}
                                                />
                                            </>
                                        )}
                                        {googleAuth && (
                                            <>
                                                <Trans>
                                                    Check your authenticator app and enter the
                                                    6-digit code you see there.
                                                </Trans>{' '}
                                                <Button
                                                    variant="link"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href=" https://support.google.com/accounts/answer/1066447?hl=en&ref_topic=2954345"
                                                >
                                                    <Trans>Learn more</Trans>
                                                </Button>
                                            </>
                                        )}
                                    </Text>
                                    <Inline alignX="left">
                                        <Checkbox
                                            checked={isTrusted}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setIsTrusted(e.target.checked)
                                            }
                                        >
                                            <Trans>Remember me in this browser</Trans>
                                        </Checkbox>
                                    </Inline>
                                    <CodeInput
                                        initialValue={previousOtp}
                                        hasError={hasError}
                                        onSubmit={onSubmitOtp}
                                    />

                                    <Inline space={18} alignY="center" alignX="left">
                                        {hasError && (
                                            <Text variant="medium-default" color="red700">
                                                <Trans>Wrong code, try again</Trans>
                                            </Text>
                                        )}
                                        {!googleAuth && !hasError && (
                                            <>
                                                <Text variant="medium-default">
                                                    <Trans>Code expires in</Trans>{' '}
                                                    {isTokenVerifying && (
                                                        <s.TimerFragmentWrapper>
                                                            {timer.minutes}:{timer.seconds}
                                                        </s.TimerFragmentWrapper>
                                                    )}
                                                </Text>

                                                <Button
                                                    variant="link"
                                                    onClick={resendToken}
                                                    disabled={isResendDisabled}
                                                >
                                                    <Trans>Resend</Trans>
                                                </Button>
                                            </>
                                        )}
                                    </Inline>
                                </>
                            )}
                            {isTokenExpired && (
                                <>
                                    <Text variant="3xlarge-accent">
                                        <Trans>Code expired</Trans>
                                    </Text>
                                    <Text variant="medium-default">
                                        <Trans>
                                            The code we've sent to your phone number ending in{' '}
                                            {phoneLastDigits} expired, get a new one to log in.
                                        </Trans>{' '}
                                        <ContactSupport
                                            chatLabel={t`New number?`}
                                            emailLabel={t`New number? Contact support@pleo.io`}
                                        />
                                    </Text>
                                    <Button variant="primary" onClick={resendToken}>
                                        <Trans>Resend</Trans>
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </section>
                </s.Form>
                <s.BottomNav>
                    <Button variant="link" onClick={navigateBack}>
                        &larr; <Trans>Back</Trans>
                    </Button>
                </s.BottomNav>
            </s.Content>
            <ColumnImage>
                {!isTokenExpired && !googleAuth && (
                    <img
                        src={VerifyIllustration}
                        alt={t`Drawn bird wearing glasses is telegraphing a message`}
                    />
                )}
                {!isTokenExpired && googleAuth && (
                    <img
                        src={AuthAppIllustration}
                        alt={t`Drawn bird wearing glasses is pressing buttons on telephone`}
                    />
                )}
                {isTokenExpired && (
                    <img
                        src={ExpiredIllustration}
                        alt={t`Drawn bird is sitting by a telegraph with its eyes closed and is waving its wing dismissively`}
                    />
                )}
            </ColumnImage>
        </s.Wrapper>
    )
}

/**
 * Login Flow
 * This will support both legacy and the new 2FA way of doing things
 * Part I
 * (1) Enter email => (2) Check user email (We make a call to the backend to check the current login supported for that email)
 * Part II
 * Current (Account DOES NOT have a verified phone number) => (1) Enter password => Gain Access
 * New flow (Account has verified phone number) => (1) Ask for passcode => (2) Request Otp (one time password) => (3) Gain Access
 *
 */
export function LoginFlow() {
    const {
        state: {screen},
    } = useLoginLocationState()

    const element = React.useMemo(() => {
        let el
        switch (screen) {
            case LoginScreen.PASSCODE:
                el = <PasscodeScreen />
                break
            case LoginScreen.PASSWORD:
                el = <PasswordScreen />
                break
            case LoginScreen.VALIDATION:
                el = <ValidationScreen />
                break
            default:
                el = <EmailScreen />
        }

        return el
    }, [screen])

    return (
        <AnimatePresence initial={false}>
            <motion.div
                key={screen ?? undefined}
                initial={screen ? screenAnimations.initial : undefined}
                animate={screenAnimations.animate}
                exit="initial"
            >
                {element}
            </motion.div>
        </AnimatePresence>
    )
}

const screenAnimations = {
    initial: {
        x: 0,
        opacity: 0,
        transition: {
            opacity: tokens.slowOut,
        },
    },
    animate: {
        x: 0,
        opacity: 1,
        transition: {
            opacity: tokens.smoothIn,
        },
    },
}
